/* eslint-disable react-hooks/exhaustive-deps */
import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../../redux/hooks';
import useResponsiveScreenSwitch from "../../../utils/useResponsiveScreenSwitch";
import { MOBILE_SX } from '../../../mobile/mobileApp';

/**
 * Phone: the app's content area already has its gutter and scrolls, so the
 * account card drops its own padding and inner scroll, the Profile/Password
 * tabs become a full-width segmented control, and the profile sections below
 * stack as full-width cards with 40px edit buttons. `&&` doubles the root's
 * class so these rules outrank custom.scss and profile.scss (whose selectors
 * have two classes too) by specificity, whatever order the styles load in.
 */
const phoneProfileSx = {
  [MOBILE_SX]: {
    '&.profileSettingsPage': { height: 'auto', padding: 0, overflow: 'visible' },
    '&& .settingsHeader': { padding: '12px', gap: '12px' },
    '&& .settingsTitle': { fontSize: '18px' },
    '&& .settingsTab': { minHeight: '40px', fontSize: '14px' },
    '&& .settingsBody': { padding: '12px', overflow: 'visible' },
    '&& .profilePageLayout': { padding: 0, gap: '12px' },
    '&& .profileRow': { flexDirection: 'column', gap: '12px' },
    '&& .profileCardHeader': { padding: '8px 8px 8px 16px', gap: '8px' },
    '&& .profileCardTitle': { minWidth: 0 },
    '&& .profileCardHeaderAction': { width: 'auto', height: 'auto', padding: 0, flexShrink: 0 },
    '&& .profileCardActionButton': { width: '40px', height: '40px', borderRadius: '8px' },
    '&& .profileCardBody': { padding: '12px 16px 16px', gap: '12px' },
    '&& .profileInfoSection': { gap: '12px' },
    '&& .profileAvatarSquare': { width: '56px', height: '56px', flexShrink: 0, fontSize: '24px' },
    // Long emails and names wrap inside the card instead of running off it.
    '&& .profileInfoDetails': { minWidth: 0, gap: '6px' },
    '&& .profileInfoName, && .profileBodyText': { overflowWrap: 'anywhere' }
  }
};

export interface FormValues {
  email: string;
  country: string;
  firstName: string;
  lastName: string;
  timezone: string;
  headshot: string;
  dob: string;
  phoneNumber: string;
  phonePrefix: string;
  gender: string;
  ethnicity: string;
  firstGenStudent: string;
  social: {
    facebook: string;
    instagram: string;
    youtube: string;
    twitter: string;
    linkedIn: string;
  };
  disability: string[];
  category: string;
  hobbies: string[];
}

export const initialValues: FormValues = {
  email: '',
  firstName: '',
  country: '',
  lastName: '',
  timezone: '',
  headshot: '',
  dob: '',
  phoneNumber: '',
  phonePrefix: '',
  gender: '',
  ethnicity: '',
  firstGenStudent: '',
  social: {
    facebook: '',
    instagram: '',
    youtube: '',
    twitter: '',
    linkedIn: ''
  },
  disability: [],
  category: '',
  hobbies: []
};

const ProfileLayout = () => {
  const userDetails = useAppSelector((state) => state.userProfile);
  const { forMobile } = useResponsiveScreenSwitch();
  const navigate = useNavigate();
  const location = useLocation();

  const tabs = useMemo(() => {
    const baseTabs = [
      { id: 'profile', label: 'Profile', route: '/app/profile' }
    ];

    if (userDetails.data?.connection === 'Username-Password-Authentication') {
      baseTabs.push({
        id: 'security',
        label: 'Password',
        route: '/app/profile/security'
      });
    }

    return baseTabs;
  }, [userDetails.data?.connection]);

  const activeRoute = useMemo(() => {
    const normalizedPath = location.pathname.replace(/\/$/, '');
    const sortedTabs = [...tabs].sort((a, b) => b.route.length - a.route.length);
    const match = sortedTabs.find(
      (tab) => normalizedPath === tab.route || normalizedPath.startsWith(`${tab.route}/`)
    );
    return match?.route || '';
  }, [location.pathname, tabs]);

  const handleNavigate = (route: string) => {
    if (location.pathname !== route) {
      navigate(route);
    }
  };

  const isActive = (route: string) => activeRoute === route;

  /** For responsive */
  if (userDetails.error) {
    return <Box>{userDetails.errorText}</Box>;
  }

  if (userDetails.data) {
    return (
      <Box className="profileSettingsPage" sx={phoneProfileSx}>
        <Box className='settingsCardShell'>
          <Box className='settingsHeader'>
            <Typography className="settingsTitle">My account</Typography>
            <Box className="settingsHeaderActions" flexDirection={forMobile ? 'column' : 'row'}>
              <Box className="settingsTabs" width={forMobile ? '100%' : 'auto'}>
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    className={`settingsTab ${isActive(tab.route) ? 'active' : ''}`}
                    onClick={() => handleNavigate(tab.route)}
                    type="button"
                  >
                    {tab.label}
                  </button>
                ))}
              </Box>
            </Box>
          </Box>
          <Box className='settingsBody'>
            <Outlet />
          </Box>
        </Box>
      </Box>
    );
  }

  return null;
};

export default ProfileLayout;
